import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { take } from 'rxjs/operators';
import { ChatWidgetService, ChatAction } from './chat-widget.service';
import {
  submitSearch,
  changeActiveTab,
  changeActiveSideTab,
  navigateToResults,
} from '../../state/action';
import { selectTabList } from '../../state/selectors';
import { AppState, Tab } from '../../state/app.state';
import {
  CompanyThemePopupComponent,
  CompanyThemePopupData,
} from '../results/components/result-sections/general/company-theme-popup/company-theme-popup.component';

@Component({
  selector: 'app-chat-widget',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './chat-widget.component.html',
  styleUrls: ['./chat-widget.component.css'],
})
export class ChatWidgetComponent implements OnInit, OnDestroy {
  @ViewChild('chatBox') private chatBox!: ElementRef<HTMLDivElement>;

  userInput = '';
  shaking = false;
  private shakeInterval: any;
  private scrollPollInterval: any;

  suggestions = [
    'How is NVIDIA performing in 3D imaging?',
    'Find solid state battery clusters',
    'What is the innovation score?',
    'Take me to technology trends',
  ];

  constructor(
    public svc: ChatWidgetService,
    private store: Store<AppState>,
    private router: Router,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.startShakeTimer();
  }

  ngOnDestroy() {
    clearInterval(this.shakeInterval);
    clearInterval(this.scrollPollInterval);
  }

  private startShakeTimer() {
    this.shakeInterval = setInterval(() => {
      if (!this.svc.isOpen && !this.svc.hasUnread) {
        this.shaking = true;
        setTimeout(() => (this.shaking = false), 800);
      }
    }, 25000);
  }

  toggle() {
    this.svc.toggle();
    if (this.svc.isOpen) {
      this.scrollToBottom();
    }
  }

  send() {
    if (!this.userInput.trim() || this.svc.isLoading) return;
    const text = this.userInput;
    this.userInput = '';
    this.svc.sendMessage(text);
    this.scrollToBottom();

    clearInterval(this.scrollPollInterval);
    this.scrollPollInterval = setInterval(() => {
      if (!this.svc.isLoading) {
        clearInterval(this.scrollPollInterval);
        this.scrollToBottom();
      }
    }, 200);
  }

  selectSuggestion(s: string) {
    this.userInput = s;
    this.send();
  }

  navigate(action: ChatAction) {
    if (action.type === 'company_theme' && action.entityid && action.cluster_id) {
      const data: CompanyThemePopupData = {
        entityid: action.entityid,
        entity_name: action.entity_name || '',
        theme_label: action.cluster_name || 'Theme',
        theme_search_type: 'cluster',
        cluster_id: action.cluster_id,
        cluster_name: action.cluster_name || null,
        query: null,
        tabId: `chat-${action.entityid}-${action.cluster_id}`,
      };
      this.dialog.open(CompanyThemePopupComponent, {
        width: '90vw',
        height: '85vh',
        maxWidth: '1200px',
        maxHeight: '800px',
        position: { top: '80px' },
        data,
      });
      return;
    }
    if (action.type === 'page' && action.route) {
      this.router.navigate([action.route]);
      return;
    }

    // For company/theme nav, first check if a matching tab is already open — if so,
    // switch to it + update sidetab instead of creating a duplicate tab.
    this.store
      .select(selectTabList)
      .pipe(take(1))
      .subscribe((tabs) => {
        const existing = this.findMatchingTab(tabs as Tab[], action);
        if (existing) {
          this.store.dispatch(changeActiveTab({ tabId: existing.tabId }));
          if (action.sideTab) {
            this.store.dispatch(changeActiveSideTab({ sideTab: action.sideTab }));
          }
          this.store.dispatch(navigateToResults());
          return;
        }
        this.dispatchSubmitSearch(action);
      });
  }

  private findMatchingTab(tabs: Tab[], action: ChatAction): Tab | undefined {
    if (action.type === 'company' && action.entityid) {
      return tabs.find(
        (t) => t.search_type === 'company' && t.params?.entityids?.[0] === action.entityid,
      );
    }
    if (action.type === 'theme' && action.cluster_id) {
      return tabs.find(
        (t) =>
          t.search_type === 'theme' &&
          t.params?.cluster_id != null &&
          String(t.params.cluster_id) === String(action.cluster_id),
      );
    }
    if (action.type === 'theme' && action.query) {
      return tabs.find(
        (t) => t.search_type === 'theme' && t.params?.query === action.query,
      );
    }
    return undefined;
  }

  private dispatchSubmitSearch(action: ChatAction) {
    if (action.type === 'company' && action.entityid) {
      this.store.dispatch(
        submitSearch({
          searchParams: {
            companies: [{
              entityid: action.entityid,
              entity_name: action.entity_name || '',
              gsubind: action.gsubind || '',
              companytype: action.companytype || 'Public Company',
            }],
          },
          searchType: 'company',
          sideTab: action.sideTab,
        }),
      );
    } else if (action.type === 'theme' && action.cluster_id) {
      this.store.dispatch(
        submitSearch({
          searchParams: {
            themes: [{
              theme_search_type: 'cluster',
              cluster_id: action.cluster_id,
              cluster_name: action.theme_name || 'Theme',
              api_query: null,
              name: action.theme_name || 'Theme',
            }],
          },
          searchType: 'theme',
          sideTab: action.sideTab,
        }),
      );
    } else if (action.type === 'theme' && action.query) {
      this.store.dispatch(
        submitSearch({
          searchParams: {
            themes: [{
              api_query: action.query,
              name: action.theme_name || 'Theme',
            }],
          },
          searchType: 'theme',
          sideTab: action.sideTab,
        }),
      );
    }
  }

  trackByIndex(index: number) {
    return index;
  }

  getActionIcon(action: ChatAction): string {
    if (action.type === 'company') return 'bi-building';
    if (action.type === 'theme') return 'bi-diagram-3';
    if (action.type === 'company_theme') return 'bi-zoom-in';
    return 'bi-box-arrow-up-right';
  }

  private scrollToBottom() {
    setTimeout(() => {
      if (this.chatBox?.nativeElement) {
        this.chatBox.nativeElement.scrollTop = this.chatBox.nativeElement.scrollHeight;
      }
    }, 50);
  }
}
